import React, { useContext, useEffect, useRef, useState } from 'react'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import { useHubFailure, useShownHub } from 'hooks/useShownHub'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import { PROJECT, ProjectRoles, projectRoleLabel } from 'app-constants'
import { useSearchParams } from 'react-router-dom'
import { capitaliseFirstLetter, validateSourceUrl } from 'helpers/utils'
import { formatShortDate } from 'helpers/date'
import { IMemberInvite, IProjectMember, ITableWithCells } from 'types'
import Button from 'components/button'
import Input from 'components/input'
import FilterMenu from 'components/filter-menu'
import Tabs from 'components/tabs'
import HoverCard from 'components/hover-card'
import { BLUE_BUTTON } from 'components/save-buttons'
import ItemCell, { ItemAvatar } from 'components/data-table/itemCell'
import DeleteModal from 'components/delete'
import { Delete, Email, Lock, Pencil, Plus, Resend } from 'components/icons'
import Tooltip from 'components/tooltip'
import DataTable, { dataTableColumns, DataTableColumn } from 'components/data-table'
import RowActions from 'components/data-table/rowActions'
import DateCell from 'components/data-table/dateCell'
import InviteDialog from 'views/settings/components/inviteDialog'
import { SETTINGS_CANVAS, SettingsCard, TextLink } from 'views/settings/components/settingsCard'
import EditRoleDialog from 'views/settings/components/permissions/editRoleDialog'
import {
  accessLabel,
  inviteRow,
  memberRow,
  memberTagsIn,
  PersonRow,
  canRemove,
  memberMenuNote,
  rolesFor,
  rowMatches,
  rowName,
  rowRole,
  rowTagsIn,
  tagGroups
} from 'views/settings/components/permissions/utils'

const NEWEST_FIRST = [{ id: 'createdAt', desc: true }]
// Wide enough for the longest day, so the columns hold still as rows come and go.
const DATE_COLUMN = 'w-[124px]'

/** Which rows the tabs show: everyone, the members who've joined, or the pending invites. */
type Show = 'all' | 'active' | 'invited'
const SHOWS: Record<Show, (row: PersonRow) => boolean> = {
  all: () => true,
  active: (row) => row.kind === 'member',
  invited: (row) => row.kind === 'invite'
}

/** What the cells need from the page: who's looking, and how to change a member's role, tags or membership. */
interface MembersActions {
  viewerRole?: ProjectRoles
  viewerEmail?: string
  editRole: (member: IProjectMember) => void
  askRemove: (member: IProjectMember) => void
  setTags: (member: IProjectMember, tagIds: string[], tableId: string) => void
  /** The invites being resent. */
  resending: string[]
  resendInvite: (invite: IMemberInvite) => void
  editInvite: (invite: IMemberInvite) => void
  askDeleteInvite: (invite: IMemberInvite) => void
}

const Members = React.createContext<MembersActions>({
  editRole: () => undefined,
  askRemove: () => undefined,
  setTags: () => undefined,
  resending: [],
  resendInvite: () => undefined,
  editInvite: () => undefined,
  askDeleteInvite: () => undefined
})

/** The member's picture, their name (opening their profile in a new tab) and their email under it. */
const MemberCell: React.FC<{ member: IProjectMember }> = ({ member }) => (
  <ItemCell
    media={<ItemAvatar src={validateSourceUrl(member.user.profilePicture)} />}
    title={
      <a
        href={`/profile/${member.user.firebaseUserId}/information`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium! text-body hover:underline! underline-offset-2"
      >
        {member.user.name}
      </a>
    }
    description={member.user.email}
  />
)

/**
 * How the member came to be in the hub, with a card on hover saying what that means: added to the hub
 * itself, or an admin of the hub's organisation or site, whose access is managed there.
 */
const AccessCell: React.FC<{ member: IProjectMember }> = ({ member }) => {
  const { project } = useProject()
  const site = project.site?.name
  const organisation = project.site?.organisation?.name
  const card = {
    direct: {
      title: 'Direct member',
      content: `Member was invited or joined this ${PROJECT} directly. The member's role can be updated directly from the ${PROJECT}'s settings. They can be removed from this ${PROJECT} here.`
    },
    organisation: {
      title: 'Organisation admin',
      content: `Member is an admin of ${organisation ?? 'the organisation'} that this ${PROJECT}'s site belongs to. Organisation admins are Owners of every ${PROJECT} in the organisation. Their role and membership can be managed in the organisation's settings.`
    },
    site: {
      title: 'Site admin',
      content: `Member is an admin of ${site ? `the ${site} site` : 'the site'} that this ${PROJECT} belongs to. Site admins are Owners of every ${PROJECT} in the site. Their role and membership can be managed in the site's settings.`
    }
  }[member.roleAssignedMethod]
  return (
    <HoverCard title={card.title} content={card.content}>
      {accessLabel(memberRow(member))}
    </HoverCard>
  )
}

/** Why a member's role is managed somewhere other than here, said helpfully; null when it isn't. */
const lockedBecause = (member: IProjectMember, viewerEmail: string | undefined): string | null => {
  if (member.roleAssignedMethod === 'organisation') return "This role can be managed in the organisation's settings."
  if (member.roleAssignedMethod === 'site') return "This role can be managed in the site's settings."
  if (member.user.email === viewerEmail) return 'Your role can be changed by another Owner.'
  return null
}

/**
 * The member's role. Where the person viewing may change it, pointing at it shows a pencil that opens the
 * Edit Role dialog; where it's managed elsewhere, a lock says where.
 */
const RoleCell: React.FC<{ member: IProjectMember }> = ({ member }) => {
  const { viewerRole, viewerEmail, editRole } = useContext(Members)
  const locked = lockedBecause(member, viewerEmail)
  const editable = rolesFor(member, viewerRole, viewerEmail).length > 0
  return (
    <span className="group/role flex items-center gap-1">
      {projectRoleLabel(member.role)}
      {editable && (
        <Tooltip content="Edit role">
          <Button
            internalType="transparent"
            aria-label={`Edit role of ${member.user.name}`}
            // Shown on pointing at the role, and whenever it has focus.
            className="size-6 p-0 opacity-0 group-hover/role:opacity-100 focus-visible:opacity-100 [&_svg]:size-3.5"
            onClick={() => editRole(member)}
          >
            <Pencil />
          </Button>
        </Tooltip>
      )}
      {!editable && locked && (
        <Tooltip content={locked}>
          <span className="flex text-muted" role="img" aria-label={locked}>
            <Lock className="size-3.5" />
          </span>
        </Tooltip>
      )}
    </span>
  )
}

/**
 * The member's tags from one tag table, as a button saying them that opens our menu to tick and untick
 * them, grouped by their column, with a search.
 */
const TagsCell: React.FC<{ member: IProjectMember; tagTable: ITableWithCells }> = ({ member, tagTable }) => {
  const { setTags } = useContext(Members)
  const tags = memberTagsIn(member, tagTable)
  const selected = tags.map((tag) => tag.referencePublicId)
  return (
    <div data-tour="member-tag-select">
      <FilterMenu
        noun={{ one: 'tag', many: 'tags' }}
        groups={tagGroups(tagTable)}
        selected={selected}
        onChange={(tagIds) => setTags(member, tagIds ?? [], tagTable.publicId)}
        pick
        label={`${tagTable.name} tags of ${member.user.name}`}
        buttonLabel={
          tags.length === 0 ? (
            <span className="text-muted">Add tags</span>
          ) : (
            tags
              .map((tag) => tag.value)
              .sort()
              .join(', ')
          )
        }
        labelInFull={tags
          .map((tag) => tag.value)
          .sort()
          .join(', ')}
        buttonClassName="w-[220px] justify-between"
      />
    </div>
  )
}

/** Every member's ⋯ menu: Edit Role and Remove, each unavailable where it can't be done, with why at its foot. */
const MemberActions: React.FC<{ member: IProjectMember }> = ({ member }) => {
  const { viewerRole, viewerEmail, editRole, askRemove } = useContext(Members)
  return (
    <RowActions
      name={member.user.name}
      actions={[
        {
          label: 'Edit Role',
          icon: <Pencil />,
          disabled: rolesFor(member, viewerRole, viewerEmail).length === 0,
          onSelect: () => editRole(member)
        },
        {
          label: 'Remove',
          icon: <Delete />,
          destructive: true,
          disabled: !canRemove(member, viewerRole),
          onSelect: () => askRemove(member)
        }
      ]}
      footer={memberMenuNote(member, viewerRole, viewerEmail)}
      menu
    />
  )
}

/** An invite's picture: an envelope, as the person invited has no picture of their own yet. */
const InviteAvatar: React.FC = () => (
  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-hover text-muted">
    <Email />
  </span>
)

/** An invite's Access: Invited, with a card on hover saying who sent it, when, and what happens next. */
const InvitedCell: React.FC<{ invite: IMemberInvite }> = ({ invite }) => {
  const sender = invite.invitedBy.name || invite.invitedBy.email
  return (
    <HoverCard
      title="Invited"
      content={`Invited by ${sender} on ${formatShortDate(invite.createdAt)}. They join this ${PROJECT} with this role and these tags when they sign up to Morta.`}
    >
      Invited
    </HoverCard>
  )
}

/** An invite's tags from one tag table, as text: they change through Edit Invite. */
const InviteTagsCell: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (tags.length === 0) return <span className="block w-[220px] pl-[11px] text-muted">No tags</span>
  const names = tags.join(', ')
  return (
    <Tooltip content={names} showOnlyIfTruncated className="w-max max-w-[320px] whitespace-normal">
      {/* In line with the members' tag buttons' text, inside their border and padding. */}
      <span className="block w-[220px] truncate pl-[11px] leading-5">{names}</span>
    </Tooltip>
  )
}

/** An invite's ⋯ menu: Resend Invite, Edit Invite and Delete Invite. */
const InviteActions: React.FC<{ invite: IMemberInvite }> = ({ invite }) => {
  const { resending, resendInvite, editInvite, askDeleteInvite } = useContext(Members)
  return (
    <RowActions
      name={invite.email}
      actions={[
        {
          label: 'Resend Invite',
          icon: <Resend />,
          loading: resending.includes(invite.publicId),
          onSelect: () => resendInvite(invite)
        },
        { label: 'Edit Invite', icon: <Pencil />, onSelect: () => editInvite(invite) },
        { label: 'Delete Invite', icon: <Delete />, destructive: true, onSelect: () => askDeleteInvite(invite) }
      ]}
      menu
    />
  )
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
// Each cell is a member's or an invite's; an invite's role and tags are read only, changed by Edit Invite.
const col = dataTableColumns<PersonRow>()
const LEADING_COLUMNS = col.columns([
  col.accessor(rowName, {
    id: 'name',
    header: 'Member',
    sortFn: 'alphanumeric',
    cell: ({ row }) =>
      row.original.kind === 'member' ? (
        <MemberCell member={row.original.member} />
      ) : (
        // Two lines, as a member's: the email, and who sent the invite under it.
        <ItemCell
          media={<InviteAvatar />}
          title={row.original.invite.email}
          description={`Invited by ${row.original.invite.invitedBy.name || row.original.invite.invitedBy.email}`}
        />
      )
  }),
  col.accessor(accessLabel, {
    id: 'access',
    header: 'Access',
    sortFn: 'alphanumeric',
    cell: ({ row }) =>
      row.original.kind === 'member' ? (
        <AccessCell member={row.original.member} />
      ) : (
        <InvitedCell invite={row.original.invite} />
      )
  }),
  col.accessor((row) => projectRoleLabel(rowRole(row)), {
    id: 'role',
    header: 'Role',
    sortFn: 'alphanumeric',
    cell: ({ row, getValue }) =>
      row.original.kind === 'member' ? <RoleCell member={row.original.member} /> : <>{getValue()}</>
  }),
  // An invite hasn't joined: it sorts after the members, newest first, and shows no day.
  col.accessor((row) => (row.kind === 'member' ? row.member.createdAt : ''), {
    id: 'createdAt',
    header: 'Joined',
    sortFn: 'text',
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => (getValue() ? <DateCell iso={getValue()} /> : <span className="text-muted">—</span>)
  })
])
const ACTIONS_COLUMN = col.display({
  id: 'actions',
  header: () => <span className="sr-only">Actions</span>,
  meta: { className: 'w-0 text-right' },
  cell: ({ row }) =>
    row.original.kind === 'member' ? (
      <MemberActions member={row.original.member} />
    ) : (
      <InviteActions invite={row.original.invite} />
    )
})

/** A column of the tags from one tag table, sorted by the tags' names. */
const tagColumn = (tagTable: ITableWithCells) =>
  col.accessor((row) => rowTagsIn(row, tagTable).join(', '), {
    id: `tags-${tagTable.publicId}`,
    header: `${tagTable.name} Tags`,
    sortFn: 'alphanumeric',
    cell: ({ row }) =>
      row.original.kind === 'member' ? (
        <TagsCell member={row.original.member} tagTable={tagTable} />
      ) : (
        <InviteTagsCell tags={rowTagsIn(row.original, tagTable)} />
      )
  })

/** The table's columns: one for each tag table's tags among the rest. Made only when the tag tables load. */
const columnsFor = (tagTables: ITableWithCells[]): DataTableColumn<PersonRow>[] => [
  ...LEADING_COLUMNS,
  ...tagTables.map(tagColumn),
  ACTIONS_COLUMN
]
const COLUMNS_WITHOUT_TAGS = columnsFor([])

const memberCount = (count: number) => `${count} ${count === 1 ? 'member' : 'members'}`

/**
 * What the table shows, under it, for the tab open: "9 members, 6 invited" on All, "9 members" on Active,
 * "6 invited" on Invited; "3 of 9 members" and so on when a search or the role filter narrows it.
 */
const peopleSummary = (shown: PersonRow[], members: number, invites: number, show: Show): string => {
  const shownMembers = shown.filter((row) => row.kind === 'member').length
  const shownInvites = shown.length - shownMembers
  const part = (count: number, of: number, words: (n: number) => string) =>
    count === of ? words(of) : `${count} of ${words(of)}`
  const membersPart = part(shownMembers, members, memberCount)
  const invitesPart = part(shownInvites, invites, (n) => `${n} invited`)
  if (show === 'invited') return invitesPart
  if (show === 'active' || invites === 0) return membersPart
  return `${membersPart}, ${invitesPart}`
}

/**
 * The hub's members, the latest to join first: their role and tags, to change, and Remove for those who can
 * be removed here. Members with access through the hub's organisation or site are shaded: their role is set
 * there, and they can't be removed here, but their tags can be changed.
 */
const MembersPage: React.FC = () => {
  const { user } = useAuth()
  const { project, projectRole } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const projectId = project.publicId
  const hubName = capitaliseFirstLetter(PROJECT)
  // Only Owners can see the hub's pending invites.
  const canSeeInvites = projectRole === ProjectRoles.OWNER
  // Links to the Invites section, which this page took over, open it showing the invites. (Read through the
  // router, so the page sees the address change.)
  const [query] = useSearchParams()
  const initialShow: Show = query.get('tab') === 'invites' ? 'invited' : 'all'

  const [members, setMembers] = useState<IProjectMember[]>()
  const [invites, setInvites] = useState<IMemberInvite[]>()
  const [tagTables, setTagTables] = useState<ITableWithCells[]>([])
  const [columns, setColumns] = useState(COLUMNS_WITHOUT_TAGS)
  const [loadFailed, setLoadFailed] = useState(false)
  const [search, setSearch] = useState('')
  const [show, setShow] = useState<Show>(initialShow)
  const [inviting, setInviting] = useState(false)
  const [editingInvite, setEditingInvite] = useState<IMemberInvite | null>(null)
  const [deletingInvite, setDeletingInvite] = useState<IMemberInvite | null>(null)
  const [resending, setResending] = useState<string[]>([])
  const [editingRole, setEditingRole] = useState<IProjectMember | null>(null)
  const [removing, setRemoving] = useState<IProjectMember | null>(null)
  // Which request for the members, and for the tag tables, is the latest, so only it fills the page: one
  // still on its way from before (another hub, or an earlier load) is dropped.
  const latestMembers = useRef(0)
  const latestInvites = useRef(0)
  const latestTags = useRef(0)
  const stillShown = useShownHub(projectId)
  const hubFailed = useHubFailure(projectId)

  /**
   * Asks for the members. A first load that fails shows in place of the list, with Try again; asking again
   * after a member is changed or invited keeps the list as it was and says why.
   */
  const fetchMembers = (firstLoad: boolean) => {
    // Asked for by a change started in the hub before, that finished after switching: not here.
    if (!stillShown(projectId)) return
    latestMembers.current += 1
    const thisLoad = latestMembers.current
    api
      .getProjectMembers(projectId)
      .then((response) => {
        if (thisLoad === latestMembers.current) setMembers(response.data)
      })
      .catch((error) => {
        if (thisLoad !== latestMembers.current) return
        if (firstLoad) setLoadFailed(true)
        else displayErrorMessage(error)
      })
  }
  const refresh = () => fetchMembers(false)

  /** Asks for the pending invites, for Owners. Failing, the members still show, and it says why. */
  const fetchInvites = () => {
    // Moved on first, so a request still on its way for the last hub is dropped, even when this one can't
    // see invites and asks for none.
    latestInvites.current += 1
    const thisLoad = latestInvites.current
    if (!canSeeInvites || !stillShown(projectId)) return
    api
      .getInvitedMembers(projectId)
      .then((response) => {
        if (thisLoad === latestInvites.current) setInvites(response.data)
      })
      .catch((error) => {
        if (thisLoad !== latestInvites.current) return
        setInvites((current) => current ?? [])
        displayErrorMessage(error)
      })
  }

  const load = () => {
    setMembers(undefined)
    setInvites(undefined)
    setLoadFailed(false)
    fetchMembers(true)
    fetchInvites()
    latestTags.current += 1
    const thisLoad = latestTags.current
    api
      .getProjectTags(projectId)
      .then((response) => {
        if (thisLoad !== latestTags.current) return
        setTagTables(response.data)
        setColumns(columnsFor(response.data))
      })
      .catch((error) => {
        if (thisLoad === latestTags.current) displayErrorMessage(error)
      })
  }

  // Another hub's settings: its own members, without the last one's search or anything left open.
  useEffect(() => {
    setSearch('')
    setShow(initialShow)
    setEditingRole(null)
    setRemoving(null)
    setInviting(false)
    setEditingInvite(null)
    setDeletingInvite(null)
    setTagTables([])
    setColumns(COLUMNS_WITHOUT_TAGS)
    if (projectId !== '') load()
  }, [projectId, canSeeInvites])

  // The old Invites link only picks the tab the page opens on: a link to Members afterwards shows everyone.
  useEffect(() => {
    setShow(initialShow)
  }, [initialShow])

  const roleChanged = () => {
    if (!stillShown(projectId)) return
    setEditingRole(null)
    refresh()
  }

  const setTags = (member: IProjectMember, tagIds: string[], tableId: string) => {
    const hub = projectId
    const hubName = project.name
    api
      .bulkSetTags(member.user, hub, tagIds, tableId)
      .then((response) => {
        if (!stillShown(hub)) return
        setSnackbarMessage({ status: 'success', message: 'Tags were updated' })
        setMembers((current) =>
          current?.map((each) =>
            each.user.publicId === member.user.publicId
              ? { ...each, user: { ...each.user, tags: response.data } }
              : each
          )
        )
      })
      .catch((error) => {
        hubFailed(
          error,
          hub,
          `${member.user.name}'s tags in ${hubName} couldn't be updated. Open its settings to try again.`
        )
      })
  }

  // Failing, the dialog stays open, and says why.
  const removeMember = async () => {
    if (!removing) return
    const hub = projectId
    const hubName = project.name
    try {
      await api.deleteProjectMember(removing.user, project)
    } catch (error) {
      hubFailed(
        error,
        hub,
        `${removing.user.name} couldn't be removed from ${hubName}. Open its settings to try again.`
      )
      throw error
    }
    refresh()
  }

  const invited = () => {
    if (!stillShown(projectId)) return
    setInviting(false)
    refresh()
    fetchInvites()
    setSnackbarMessage({ status: 'success', message: 'Invitations were sent' })
  }

  const resendInvite = (invite: IMemberInvite) => {
    const hub = projectId
    const hubName = project.name
    setResending((current) => [...current, invite.publicId])
    api
      .resendProjectInvite(hub, invite.publicId)
      .then(() => {
        if (stillShown(hub)) setSnackbarMessage({ status: 'success', message: `Invite resent to ${invite.email}` })
      })
      .catch((error) => {
        hubFailed(
          error,
          hub,
          `The invite to ${invite.email} in ${hubName} couldn't be resent. Open its settings to try again.`
        )
      })
      .then(() => setResending((current) => current.filter((id) => id !== invite.publicId)))
  }

  const inviteEdited = (invite: IMemberInvite) => {
    if (!stillShown(projectId)) return
    setEditingInvite(null)
    fetchInvites()
    setSnackbarMessage({ status: 'success', message: `Invite to ${invite.email} updated` })
  }

  // Failing, the dialog stays open, and says why.
  const deleteInvite = async (invite: IMemberInvite) => {
    const hub = projectId
    const hubName = project.name
    try {
      await api.deleteProjectInvite(hub, invite.publicId)
    } catch (error) {
      hubFailed(
        error,
        hub,
        `The invite to ${invite.email} in ${hubName} couldn't be deleted. Open its settings to try again.`
      )
      throw error
    }
    if (stillShown(hub)) setInvites((current) => current?.filter((each) => each.publicId !== invite.publicId))
  }

  const rows = members && [...(invites ?? []).map(inviteRow), ...members.map(memberRow)]
  // Ready once the members, and the invites for those who see them, are here.
  const ready = !!members && (invites !== undefined || !canSeeInvites)
  const shown = (rows ?? []).filter((row) => SHOWS[show](row) && rowMatches(row, search, tagTables))
  const total = rows?.length ?? 0
  const canSearch = !loadFailed && total !== 0

  const empty = () => {
    if (total === 0)
      return (
        <>
          <div className="font-medium text-body">No members</div>
          <div className="mt-0.5">People who join this {PROJECT} will be listed here.</div>
        </>
      )
    if (show === 'invited' && invites?.length === 0)
      return (
        <>
          <div className="font-medium text-body">No pending invites</div>
          <div className="mt-0.5">People invited who haven&apos;t signed up to Morta yet are listed here.</div>
        </>
      )
    if (search.trim() !== '') return 'No members match your search.'
    return 'No members have joined yet.'
  }

  return (
    // 960px wide, or as wide as the table when its tag columns need more: the page scrolls sideways then,
    // rather than the table inside the card, so every column and each row's ⋯ stay reachable.
    <div className={`w-full min-w-fit min-h-full ${SETTINGS_CANVAS} [&_[data-slot=table-container]]:overflow-visible`}>
      <div className="mx-auto flex w-full max-w-[960px] min-w-fit flex-col gap-4">
        <SettingsCard
          title="Members"
          description={`Accounts with access to this ${PROJECT}. Certain accounts may inherit access via the site or organisation this ${PROJECT} belongs to.`}
        >
          <div className="flex items-center gap-2 px-4 py-3">
            {canSeeInvites && ready && (
              <div data-tour="members-show-tabs" className="flex">
                <Tabs
                  label="Show"
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'invited', label: 'Invited', count: invites?.length || undefined }
                  ]}
                  value={show}
                  onChange={setShow}
                />
              </div>
            )}
            {/* Held at the visible right edge when the page is wider than the window. */}
            <div className="sticky right-4 ml-auto flex items-center gap-2">
              {/* Search covers what a filter would: a role ("builder") or access ("site", "invited") as well. */}
              {canSearch && (
                <Input
                  type="search"
                  aria-label="Search members"
                  placeholder="Search by name, email, role or tag"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  // The button's corners beside it, so the row reads as one height.
                  className="h-8 w-[264px] rounded-md"
                />
              )}
              <Button
                internalType="outline"
                className={BLUE_BUTTON}
                data-tour="invite-members"
                onClick={() => setInviting(true)}
              >
                <Plus />
                Invite Members
              </Button>
            </div>
          </div>
          <Members.Provider
            value={{
              viewerRole: projectRole,
              viewerEmail: user?.email,
              editRole: setEditingRole,
              askRemove: setRemoving,
              setTags,
              resending,
              resendInvite,
              editInvite: setEditingInvite,
              askDeleteInvite: setDeletingInvite
            }}
          >
            <DataTable
              label={`${hubName} members`}
              rowLabel={rowName}
              columns={columns}
              data={ready ? shown : undefined}
              getRowId={(row) => row.id}
              initialSort={NEWEST_FIRST}
              // The column names stay at the top as a long list scrolls, as the audit log's do.
              stickyHeader
              // Nothing to count on an Invited tab with no invites: the table already says so.
              summary={
                ready &&
                total > 0 &&
                !(show === 'invited' && !invites?.length) &&
                peopleSummary(shown, members?.length ?? 0, invites?.length ?? 0, show)
              }
              empty={empty()}
              error={
                loadFailed && (
                  <>
                    <div className="font-medium text-body">Members couldn&apos;t be loaded</div>
                    <div className="mt-1">
                      <TextLink onClick={load}>Try again</TextLink>
                    </div>
                  </>
                )
              }
            />
          </Members.Provider>
        </SettingsCard>
      </div>

      {inviting && <InviteDialog onClose={() => setInviting(false)} onDone={invited} />}

      {editingInvite && (
        <InviteDialog
          invite={editingInvite}
          onClose={() => setEditingInvite(null)}
          onDone={() => inviteEdited(editingInvite)}
        />
      )}

      {deletingInvite && (
        <DeleteModal
          id="invite-delete-modal"
          open
          setOpen={(open) => !open && setDeletingInvite(null)}
          resourceName={deletingInvite.email}
          customHeading="Delete Invite"
          confirmLabel="Delete Invite"
          confirmByTyping={false}
          otherContent={
            <p className="text-[13px] text-muted">
              Delete the invite sent to {deletingInvite.email}? They won&apos;t be added to this {PROJECT} when they
              sign up unless you invite them again.
            </p>
          }
          deleteResource={() => deleteInvite(deletingInvite)}
        />
      )}

      {editingRole && (
        <EditRoleDialog
          member={editingRole}
          roles={rolesFor(editingRole, projectRole, user?.email)}
          onClose={() => setEditingRole(null)}
          onSaved={roleChanged}
        />
      )}

      {removing && (
        <DeleteModal
          id="remove-user-modal"
          open
          setOpen={(open) => !open && setRemoving(null)}
          customHeading={`Remove ${removing.user.name} from this ${PROJECT}?`}
          resourceName={removing.user.name}
          confirmByTyping={false}
          confirmLabel={`Remove From ${hubName}`}
          submittingLabel="Removing…"
          otherContent={
            <p className="text-[13px] text-muted">
              {removing.user.name} will no longer be able to open this {PROJECT} or its documents and tables. They can
              be invited again later.
            </p>
          }
          deleteResource={removeMember}
        />
      )}
    </div>
  )
}

export default MembersPage
